import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch } from 'react-redux';
import { FsModal } from '../common/FsModal';
import { WarningIcon } from '../styles/svg/svgIcons';
import { getSessionTimeoutConfig } from '../app/appActions';
import { isResponseFulfilled } from '../utils/apiResponseUtil';
import {
  clearSessionTimeoutConfigCache,
  getDefaultSessionTimeoutConfig,
  getInitialSessionTimeoutConfig,
  isValidSessionTimeoutConfig,
  mapApiConfigToSessionValues,
  setCachedSessionTimeoutConfigFromApi,
} from './sessionTimeoutService';

const SESSION_CONTINUE_EVENT = 'session-continue';
const SESSION_LOGOUT_EVENT = 'session-logout';
const initialConfig = getInitialSessionTimeoutConfig();

export default function SessionTimeout({ logout, refreshSession }) {
  const [sessionConfig, setSessionConfig] = useState({
    idleTimeMs: initialConfig.idleTimeMs,
    promptTimeoutSec: initialConfig.promptTimeoutSec,
  });
  const [configReady, setConfigReady] = useState(initialConfig.configReady);
  const [isPromptVisible, setIsPromptVisible] = useState(false);

  const { idleTimeMs, promptTimeoutSec } = sessionConfig;
  const [countdown, setCountdown] = useState(promptTimeoutSec);
  const lastActivityRef = useRef(Date.now());
  const hasTimedOutRef = useRef(false);

  const dispatch = useDispatch();

  const onLogout = useCallback(() => {
    hasTimedOutRef.current = true;
    setIsPromptVisible(false);
    clearSessionTimeoutConfigCache();
    logout();
    localStorage.setItem(SESSION_LOGOUT_EVENT, Date.now().toString());
  }, [logout]);

  const onPrompt = useCallback(() => {
    setIsPromptVisible(true);
    setCountdown(promptTimeoutSec);
  }, [promptTimeoutSec]);

  const onIdle = useCallback(() => {
    if (hasTimedOutRef.current) {
      return;
    }

    hasTimedOutRef.current = true;
    setIsPromptVisible(false);
    clearSessionTimeoutConfigCache();
    logout();
  }, [logout]);

  const markUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const handleStayLoggedIn = useCallback(() => {
    hasTimedOutRef.current = false;
    lastActivityRef.current = Date.now();
    setIsPromptVisible(false);
    activate();
    refreshSession();

    localStorage.setItem(SESSION_CONTINUE_EVENT, Date.now().toString());
  }, [activate, refreshSession]);

  const { activate } = useIdleTimer({
    timeout: idleTimeMs,
    promptBeforeIdle: promptTimeoutSec * 1000,
    onPrompt,
    onIdle,
    onAction: markUserActivity,
    onActive: markUserActivity,
    debounce: 500,
    crossTab: true,
    syncTimers: 200,
    disabled: !configReady,
  });

  useEffect(() => {
    let cancelled = false;

    dispatch(getSessionTimeoutConfig())
      .then((res) => {
        if (cancelled) {
          return;
        }

        if (isResponseFulfilled(res)) {
          const config = mapApiConfigToSessionValues(res.payload);

          if (isValidSessionTimeoutConfig(config)) {
            setCachedSessionTimeoutConfigFromApi(res.payload);
            setSessionConfig(config);
          } else {
            setSessionConfig(getDefaultSessionTimeoutConfig());
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSessionConfig(getDefaultSessionTimeoutConfig());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setConfigReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isPromptVisible) {
      setCountdown(promptTimeoutSec);
    }
  }, [promptTimeoutSec, isPromptVisible]);

  useEffect(() => {
    let interval;
    if (isPromptVisible && countdown > 0) {
      interval = window.setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      window.clearInterval(interval);
    };
  }, [isPromptVisible, countdown]);

  useEffect(() => {
    if (!configReady) {
      return undefined;
    }

    const handleResumeFromSleep = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const elapsedSinceActivity = Date.now() - lastActivityRef.current;
      const promptThresholdMs = idleTimeMs - promptTimeoutSec * 1000;

      if (elapsedSinceActivity >= idleTimeMs) {
        onIdle();
      } else if (elapsedSinceActivity >= promptThresholdMs) {
        onPrompt();
      } else {
        activate();
      }
    };

    document.addEventListener('visibilitychange', handleResumeFromSleep);
    window.addEventListener('focus', handleResumeFromSleep);

    return () => {
      document.removeEventListener('visibilitychange', handleResumeFromSleep);
      window.removeEventListener('focus', handleResumeFromSleep);
    };
  }, [activate, configReady, idleTimeMs, onIdle, onPrompt, promptTimeoutSec]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === SESSION_CONTINUE_EVENT) {
        setIsPromptVisible(false);
        setCountdown(promptTimeoutSec);
        activate();
      } else if (event.key === SESSION_LOGOUT_EVENT) {
        setIsPromptVisible(false);
        logout();
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [activate, logout, promptTimeoutSec]);

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  useEffect(() => {
    if (countdown === 0 && isPromptVisible) {
      onIdle();
    }
  }, [countdown, isPromptVisible, onIdle]);

  return (
    <FsModal
      show={isPromptVisible}
      closable={false}
      actions={{
        centerAlign: [
          {
            label: 'Continue Session',
            variant: 'secondary',
            onClick: handleStayLoggedIn,
          },
          {
            label: 'Logout',
            variant: 'outline-light',
            onClick: onLogout,
          },
        ],
      }}
    >
      <div className="text-center">
        <span className="d-block text-danger">
          <WarningIcon />
        </span>

        <div className="d-block f-18 mt-4 mb-3 text-grey-5">
          <h3>Session Timeout</h3>
          <p>
            Your session is about to expire in{' '}
            <strong>
              {minutes?.toString()?.padStart(2, '0')}:{seconds?.toString()?.padStart(2, '0')}
            </strong>{' '}
            (mm:ss).
          </p>
          <p>Please click on Continue Session or Logout to end this session.</p>
        </div>
      </div>
    </FsModal>
  );
}
